import { useEffect, useRef } from 'react'

/**
 * Close on a click outside, or on Escape.
 *
 * Every popover on a queue table needs both — a menu that only closes by
 * clicking its own trigger again is one the reader leaves open and then
 * scrolls the table underneath. Shared rather than written twice, because the
 * second copy is the one that gets the keyboard half wrong.
 */
export function useDismiss<T extends HTMLElement>(
  open: boolean,
  close: () => void,
  /** A part of the popover drawn elsewhere on the page — a menu list placed at the
   *  body so no scrolling frame clips it. A click there is not a click away. */
  also?: React.RefObject<HTMLElement | null>,
) {
  const box = useRef<T>(null)

  useEffect(() => {
    if (!open) return

    function away(event: MouseEvent) {
      const target = event.target as Node
      if (!box.current?.contains(target) && !also?.current?.contains(target)) close()
    }
    function key(event: KeyboardEvent) {
      if (event.key === 'Escape') close()
    }

    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', key)
    }
  }, [open, close])

  return box
}
